import { FormControl, FormGroup } from '@angular/forms';

import {
  PositionAssignment,
  PositionAssignmentStatus,
  PositionHistoryEntryPayload,
} from '@core/tellent-positions-types';
import { PositionRowForm } from '@core/tellent-positions-ui';

export function makePositionRow(assignment?: PositionAssignment | null): PositionRowForm {
  return new FormGroup({
    id: new FormControl<number | null>(assignment?.id ?? null),
    positionGuid: new FormControl<string | null>(assignment?.positionGuid ?? null),
    startDate: new FormControl<string | null>(assignment?.startDate ?? null),
  });
}

export function positionRowValue(assignment?: PositionAssignment | null) {
  return {
    id: assignment?.id ?? null,
    positionGuid: assignment?.positionGuid ?? null,
    startDate: assignment?.startDate ?? null,
  };
}

export function isRowComplete(row: PositionRowForm): boolean {
  return !!row.controls.positionGuid.value && !!row.controls.startDate.value;
}

// A row nobody filled in is dropped rather than blocking the save, so a member without a current
// position is not forced to invent one.
export function isRowEmpty(row: PositionRowForm): boolean {
  return !row.controls.positionGuid.value && !row.controls.startDate.value;
}

// The whole history as it should look afterwards: rows carrying an id are rewritten, rows without
// one are created, and the ones left out are the deletions. Each row states the status of the
// section it sits in rather than leaving the backend to read one out of its date.
export function historyEntries(
  current: PositionRowForm,
  scheduled: PositionRowForm[],
  previous: PositionRowForm[],
): PositionHistoryEntryPayload[] {
  return [
    ...sectionEntries([current], PositionAssignmentStatus.Current),
    ...sectionEntries(scheduled, PositionAssignmentStatus.Scheduled),
    ...sectionEntries(previous, PositionAssignmentStatus.Past),
  ];
}

export function hasHistoryChanges(
  entries: PositionHistoryEntryPayload[],
  assignments: PositionAssignment[],
): boolean {
  const storedById = new Map(assignments.map(assignment => [assignment.id, assignment]));

  if (entries.length !== storedById.size) return true;

  return entries.some(entry => {
    // A row with no id is a create, so there is nothing stored to compare it against.
    if (entry.id === null) return true;

    const stored = storedById.get(entry.id);
    return (
      !stored ||
      stored.positionGuid !== entry.positionGuid ||
      stored.startDate !== entry.startDate ||
      stored.status !== entry.status
    );
  });
}

function sectionEntries(
  rows: PositionRowForm[],
  status: PositionAssignmentStatus,
): PositionHistoryEntryPayload[] {
  return rows.filter(isRowComplete).map(row => ({
    id: row.controls.id.value,
    positionGuid: row.controls.positionGuid.value!,
    startDate: row.controls.startDate.value!,
    status,
  }));
}

export function startDatesByRow(rows: PositionRowForm[]): Map<PositionRowForm, string | null> {
  return new Map(rows.map(row => [row, row.controls.startDate.value]));
}

export function rowsWithChangedStartDate(
  rows: PositionRowForm[],
  previousDates: Map<PositionRowForm, string | null>,
): PositionRowForm[] {
  return rows.filter(row => previousDates.get(row) !== row.controls.startDate.value);
}

export function latestStartDate(rows: PositionRowForm[]): string | null {
  return rows
    .map(row => row.controls.startDate.value)
    .filter((date): date is string => !!date)
    .reduce<string | null>((latest, date) => (!latest || date > latest ? date : latest), null);
}
